import React, { useState, Fragment } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPinIcon, CreditCardIcon, CheckCircleIcon, BanknoteIcon, PackageIcon, ArrowLeftIcon, SmartphoneIcon } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { OrderAddress } from '../context/StoreContext';
const INDIAN_STATES = ['Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu & Kashmir', 'Ladakh'];
export function CheckoutPage() {
  const navigate = useNavigate();
  const {
    cart,
    cartTotal,
    user,
    placeOrder,
    adminSettings
  } = useStore();
  const [step, setStep] = useState<'address' | 'payment' | 'confirmation'>('address');
  const [orderId, setOrderId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'COD' | 'UPI'>(adminSettings.codEnabled ? 'COD' : 'UPI');
  const [address, setAddress] = useState<OrderAddress>({
    name: user?.name || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof OrderAddress, string>>>({});
  const deliveryCharge = cartTotal >= 999 ? 0 : 49;
  const finalTotal = cartTotal + deliveryCharge;
  if (!user) {
    navigate('/login');
    return null;
  }
  if (cart.length === 0 && step !== 'confirmation') {
    navigate('/cart');
    return null;
  }
  const validateAddress = (): boolean => {
    const newErrors: Partial<Record<keyof OrderAddress, string>> = {};
    if (!address.name.trim()) newErrors.name = 'Name is required';
    if (!address.phone.trim()) newErrors.phone = 'Phone is required';else if (!/^\d{10}$/.test(address.phone)) newErrors.phone = 'Enter valid 10-digit phone';
    if (!address.address.trim()) newErrors.address = 'Address is required';
    if (!address.city.trim()) newErrors.city = 'City is required';
    if (!address.state) newErrors.state = 'State is required';
    if (!address.pincode.trim()) newErrors.pincode = 'Pincode is required';else if (!/^\d{6}$/.test(address.pincode)) newErrors.pincode = 'Enter valid 6-digit pincode';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handlePlaceOrder = () => {
    try {
      const id = placeOrder(address, paymentMethod);
      setOrderId(id);
      setStep('confirmation');
    } catch (err) {
      alert('Failed to place order. Please try again.');
    }
  };
  const steps = [{
    key: 'address',
    label: 'Address',
    icon: MapPinIcon
  }, {
    key: 'payment',
    label: 'Payment',
    icon: CreditCardIcon
  }, {
    key: 'confirmation',
    label: 'Confirmation',
    icon: CheckCircleIcon
  }];
  const currentStepIndex = steps.findIndex(s => s.key === step);
  return <div className="min-h-screen bg-slate-50" data-id="element-874">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8" data-id="element-875">
        {/* Header */}
        {step !== 'confirmation' && <div className="flex items-center gap-4 mb-6" data-id="element-876">
            <button onClick={() => step === 'payment' ? setStep('address') : navigate('/cart')} className="p-2 text-slate-500 hover:text-sky-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-877">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-878" />
            </button>
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-879">Checkout</h1>
          </div>}

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8" data-id="element-880">
          {steps.map((s, i) => <Fragment key={s.key} data-id="element-881">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${i <= currentStepIndex ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-400'}`} data-id="element-882">
                <s.icon className="w-4 h-4" data-id="element-883" />
                <span className="hidden sm:inline" data-id="element-884">{s.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`w-8 h-0.5 ${i < currentStepIndex ? 'bg-sky-400' : 'bg-slate-200'}`} data-id="element-885" />}
            </Fragment>)}
        </div>

        {/* Address Step */}
        {step === 'address' && <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-id="element-886">
            <div className="lg:col-span-3" data-id="element-887">
              <div className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-888">
                <h2 className="font-bold text-lg text-slate-900 mb-6" data-id="element-889">
                  Delivery Address
                </h2>
                <div className="space-y-4" data-id="element-890">
                  <div data-id="element-891">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-892">
                      Full Name *
                    </label>
                    <input type="text" value={address.name} onChange={e => setAddress({
                  ...address,
                  name: e.target.value
                })} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 ${errors.name ? 'border-red-300' : 'border-slate-200'}`} placeholder="Enter your full name" data-id="element-893" />
                    {errors.name && <p className="text-red-500 text-xs mt-1" data-id="element-894">{errors.name}</p>}
                  </div>
                  <div data-id="element-895">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-896">
                      Phone Number *
                    </label>
                    <input type="tel" value={address.phone} onChange={e => setAddress({
                  ...address,
                  phone: e.target.value.replace(/\D/g, '').slice(0, 10)
                })} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 ${errors.phone ? 'border-red-300' : 'border-slate-200'}`} placeholder="10-digit mobile number" data-id="element-897" />
                    {errors.phone && <p className="text-red-500 text-xs mt-1" data-id="element-898">
                        {errors.phone}
                      </p>}
                  </div>
                  <div data-id="element-899">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-900">
                      Address *
                    </label>
                    <textarea value={address.address} onChange={e => setAddress({
                  ...address,
                  address: e.target.value
                })} rows={3} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none ${errors.address ? 'border-red-300' : 'border-slate-200'}`} placeholder="House no., Street, Locality" data-id="element-901" />
                    {errors.address && <p className="text-red-500 text-xs mt-1" data-id="element-902">
                        {errors.address}
                      </p>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-903">
                    <div data-id="element-904">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-905">
                        City *
                      </label>
                      <input type="text" value={address.city} onChange={e => setAddress({
                    ...address,
                    city: e.target.value
                  })} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 ${errors.city ? 'border-red-300' : 'border-slate-200'}`} placeholder="City" data-id="element-906" />
                      {errors.city && <p className="text-red-500 text-xs mt-1" data-id="element-907">
                          {errors.city}
                        </p>}
                    </div>
                    <div data-id="element-908">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-909">
                        State *
                      </label>
                      <select value={address.state} onChange={e => setAddress({
                    ...address,
                    state: e.target.value
                  })} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 ${errors.state ? 'border-red-300' : 'border-slate-200'}`} data-id="element-910">
                        <option value="" data-id="element-911">Select State</option>
                        {INDIAN_STATES.map(s => <option key={s} value={s} data-id="element-912">
                            {s}
                          </option>)}
                      </select>
                      {errors.state && <p className="text-red-500 text-xs mt-1" data-id="element-913">
                          {errors.state}
                        </p>}
                    </div>
                    <div data-id="element-914">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-915">
                        Pincode *
                      </label>
                      <input type="text" value={address.pincode} onChange={e => setAddress({
                    ...address,
                    pincode: e.target.value.replace(/\D/g, '').slice(0, 6)
                  })} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 ${errors.pincode ? 'border-red-300' : 'border-slate-200'}`} placeholder="6-digit pincode" data-id="element-916" />
                      {errors.pincode && <p className="text-red-500 text-xs mt-1" data-id="element-917">
                          {errors.pincode}
                        </p>}
                    </div>
                  </div>
                </div>
                <button onClick={() => {
              if (validateAddress()) setStep('payment');
            }} className="w-full mt-6 py-3.5 bg-sky-600 text-white font-semibold rounded-xl hover:bg-sky-700 transition-colors" data-id="element-918">
                  Continue to Payment
                </button>
              </div>
            </div>
            <div className="lg:col-span-2" data-id="element-919">
              <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24" data-id="element-920">
                <h3 className="font-bold text-slate-900 mb-4" data-id="element-921">Order Summary</h3>
                <div className="space-y-3 max-h-60 overflow-y-auto mb-4" data-id="element-922">
                  {cart.map(item => <div key={`${item.product.id}-${item.size}`} className="flex gap-3" data-id="element-923">
                      <img src={item.product.images[0]} alt={item.product.name} className="w-12 h-14 object-cover rounded bg-slate-100" data-id="element-924" />
                      <div className="flex-1 min-w-0" data-id="element-925">
                        <p className="text-sm text-slate-900 font-medium truncate" data-id="element-926">
                          {item.product.name}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-927">
                          Size: {item.size} × {item.quantity}
                        </p>
                      </div>
                      <span className="text-sm font-medium text-slate-900" data-id="element-928">
                        ₹{item.product.sellingPrice * item.quantity}
                      </span>
                    </div>)}
                </div>
                <div className="border-t border-slate-100 pt-3 space-y-2 text-sm" data-id="element-929">
                  <div className="flex justify-between text-slate-600" data-id="element-930">
                    <span data-id="element-931">Subtotal</span>
                    <span data-id="element-932">₹{cartTotal}</span>
                  </div>
                  <div className="flex justify-between text-slate-600" data-id="element-933">
                    <span data-id="element-934">Delivery</span>
                    <span className={deliveryCharge === 0 ? 'text-green-600' : ''} data-id="element-935">
                      {deliveryCharge === 0 ? 'FREE' : `₹${deliveryCharge}`}
                    </span>
                  </div>
                  <div className="flex justify-between font-bold text-slate-900 pt-2 border-t border-slate-100" data-id="element-936">
                    <span data-id="element-937">Total</span>
                    <span data-id="element-938">₹{finalTotal}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Payment Step */}
        {step === 'payment' && <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} className="max-w-2xl mx-auto" data-id="element-939">
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-940">
              <h2 className="font-bold text-lg text-slate-900 mb-6" data-id="element-941">
                Payment Method
              </h2>

              <div className="space-y-4" data-id="element-942">
                {adminSettings.codEnabled && <label className={`border-2 rounded-xl p-5 flex items-start gap-4 cursor-pointer transition-colors ${paymentMethod === 'COD' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-sky-200'}`} data-id="element-943">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${paymentMethod === 'COD' ? 'border-sky-500' : 'border-slate-300'}`} data-id="element-944">
                      {paymentMethod === 'COD' && <div className="w-2.5 h-2.5 rounded-full bg-sky-500" data-id="element-945" />}
                    </div>
                    <input type="radio" name="payment" value="COD" checked={paymentMethod === 'COD'} onChange={() => setPaymentMethod('COD')} className="hidden" data-id="element-946" />
                    <div className="flex-1" data-id="element-947">
                      <div className="flex items-center gap-3" data-id="element-948">
                        <BanknoteIcon className={`w-6 h-6 ${paymentMethod === 'COD' ? 'text-sky-600' : 'text-slate-400'}`} data-id="element-949" />
                        <div data-id="element-950">
                          <h3 className="font-semibold text-slate-900" data-id="element-951">
                            Cash on Delivery (COD)
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-952">
                            Pay when your order arrives
                          </p>
                        </div>
                      </div>
                    </div>
                  </label>}

                <label className={`border-2 rounded-xl p-5 flex items-start gap-4 cursor-pointer transition-colors ${paymentMethod === 'UPI' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-sky-200'}`} data-id="element-953">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${paymentMethod === 'UPI' ? 'border-sky-500' : 'border-slate-300'}`} data-id="element-954">
                    {paymentMethod === 'UPI' && <div className="w-2.5 h-2.5 rounded-full bg-sky-500" data-id="element-955" />}
                  </div>
                  <input type="radio" name="payment" value="UPI" checked={paymentMethod === 'UPI'} onChange={() => setPaymentMethod('UPI')} className="hidden" data-id="element-956" />
                  <div className="flex-1" data-id="element-957">
                    <div className="flex items-center gap-3" data-id="element-958">
                      <SmartphoneIcon className={`w-6 h-6 ${paymentMethod === 'UPI' ? 'text-sky-600' : 'text-slate-400'}`} data-id="element-959" />
                      <div data-id="element-960">
                        <h3 className="font-semibold text-slate-900" data-id="element-961">
                          UPI Payment
                        </h3>
                        <p className="text-sm text-slate-500" data-id="element-962">
                          Pay via GPay, PhonePe, Paytm
                        </p>
                      </div>
                    </div>
                    {paymentMethod === 'UPI' && <div className="mt-4 p-4 bg-white rounded-lg border border-sky-100" data-id="element-963">
                        <p className="text-sm text-slate-700 mb-2" data-id="element-964">
                          Please transfer{' '}
                          <span className="font-bold" data-id="element-965">₹{finalTotal}</span> to
                          our UPI ID:
                        </p>
                        <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200" data-id="element-966">
                          <span className="font-mono font-medium text-slate-900" data-id="element-967">
                            {adminSettings.upiId}
                          </span>
                          <button onClick={e => {
                      e.preventDefault();
                      navigator.clipboard.writeText(adminSettings.upiId);
                      alert('UPI ID copied!');
                    }} className="text-xs text-sky-600 font-medium hover:text-sky-700" data-id="element-968">
                            Copy
                          </button>
                        </div>
                        <p className="text-xs text-slate-500 mt-3" data-id="element-969">
                          Your order will be processed once payment is
                          confirmed.
                        </p>
                      </div>}
                  </div>
                </label>
              </div>

              <div className="mt-6 p-4 bg-slate-50 rounded-xl" data-id="element-970">
                <h3 className="font-semibold text-slate-900 text-sm mb-3" data-id="element-971">
                  Delivery Address
                </h3>
                <p className="text-sm text-slate-700" data-id="element-972">{address.name}</p>
                <p className="text-sm text-slate-500" data-id="element-973">{address.address}</p>
                <p className="text-sm text-slate-500" data-id="element-974">
                  {address.city}, {address.state} - {address.pincode}
                </p>
                <p className="text-sm text-slate-500" data-id="element-975">Phone: {address.phone}</p>
                <button onClick={() => setStep('address')} className="text-sky-600 text-sm font-medium mt-2 hover:text-sky-700" data-id="element-976">
                  Change Address
                </button>
              </div>

              <div className="mt-6 p-4 bg-slate-50 rounded-xl" data-id="element-977">
                <div className="flex justify-between text-sm text-slate-600 mb-1" data-id="element-978">
                  <span data-id="element-979">Order Total</span>
                  <span className="font-bold text-slate-900 text-lg" data-id="element-980">
                    ₹{finalTotal}
                  </span>
                </div>
                <p className="text-xs text-slate-400" data-id="element-981">
                  Including delivery charges
                </p>
              </div>

              <button onClick={handlePlaceOrder} className="w-full mt-6 py-3.5 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-md shadow-green-200 flex items-center justify-center gap-2" data-id="element-982">
                <PackageIcon className="w-5 h-5" data-id="element-983" /> Place Order ({paymentMethod}
                )
              </button>
            </div>
          </motion.div>}

        {/* Confirmation Step */}
        {step === 'confirmation' && <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="max-w-lg mx-auto text-center py-12" data-id="element-984">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          delay: 0.2
        }} className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-985">
              <CheckCircleIcon className="w-10 h-10 text-green-600" data-id="element-986" />
            </motion.div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-987">
              Order Placed Successfully!
            </h2>
            <p className="text-slate-500 mb-2" data-id="element-988">
              Thank you for shopping with Sky Brand
            </p>
            <p className="text-sm font-mono bg-slate-100 inline-block px-4 py-2 rounded-lg text-slate-700 mb-6" data-id="element-989">
              Order ID: {orderId}
            </p>
            <p className="text-sm text-slate-500 mb-8" data-id="element-990">
              Estimated delivery: 5-7 business days. You'll receive updates on
              your order status.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-991">
              <Link to="/my-orders" className="px-6 py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors" data-id="element-992">
                View My Orders
              </Link>
              <Link to="/products" className="px-6 py-3 bg-white text-slate-700 font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors" data-id="element-993">
                Continue Shopping
              </Link>
            </div>
          </motion.div>}
      </div>
    </div>;
}